export function parseStockDraft(rawValue) {
  const text = String(rawValue ?? "").trim();
  if (!/^\d+$/.test(text)) {
    if (/^-\d+$/.test(text)) return { error: "Stock cannot be negative." };
    return { error: "Enter a whole-number stock quantity." };
  }

  const value = Number(text);
  if (!Number.isSafeInteger(value)) {
    return { error: "Enter a valid stock quantity." };
  }
  return { value };
}

export function buildCartEntry(entry) {
  const unitPrice = Number(entry.price) || 0;
  return {
    itemId: entry.itemId,
    variantId: entry.variantId ?? null,
    unitId: entry.unitId ?? null,
    barcode: entry.barcode,
    name: entry.name,
    quantity: 1,
    unitPrice,
    catalogUnitPrice: unitPrice,
    totalPrice: unitPrice,
    stock: Math.max(0, Number.parseInt(entry.stock, 10) || 0),
    isVariant: Boolean(entry.isVariant),
    isProductUnit: Boolean(entry.isProductUnit),
    unitName: entry.unitName || "Unit",
    conversionFactor: entry.conversionFactor || 1,
    availableUnits:
      entry.availableUnits ??
      Math.floor(Math.max(0, Number(entry.stock) || 0) / (entry.conversionFactor || 1)),
  };
}

export function buildScannedCartOption(item) {
  return {
    barcode: item.barcode,
    name: item.name,
    price: item.price,
    itemId: item.itemId ?? item.id,
    variantId: item.variantId ?? null,
    unitId: item.unitId ?? null,
    stock: item.stock,
    isVariant: Boolean(item.isVariant),
    isProductUnit: Boolean(item.isProductUnit),
    unitName: item.unitName || "Unit",
    conversionFactor: item.conversionFactor || 1,
    availableUnits: item.availableUnits,
  };
}

export function resolveLoadedCashierBarcode(items, barcode) {
  const normalizedBarcode = String(barcode ?? "").trim().toLowerCase();
  if (!normalizedBarcode || !Array.isArray(items)) return null;

  const matchedItems = items.filter(
    (item) =>
      String(item?.barcode ?? "").trim().toLowerCase() === normalizedBarcode,
  );
  if (!matchedItems.length) return null;

  if (matchedItems.length > 1) {
    return { options: matchedItems, requiresChoice: true };
  }

  const [matchedItem] = matchedItems;

  if (matchedItem.isVariant || matchedItem.isProductUnit) {
    return { options: [matchedItem], requiresChoice: false };
  }

  const itemId = String(matchedItem.itemId ?? matchedItem.id);
  const linkedVariants = items.filter(
    (item) =>
      item.isVariant && String(item.itemId ?? item.id) === itemId,
  );
  const options = [matchedItem, ...linkedVariants];

  return {
    options,
    requiresChoice: options.length > 1,
  };
}

export function getCartEntryKey(item) {
  if (item?.unitId !== undefined && item?.unitId !== null) {
    return `unit:${item.unitId}`;
  }
  const itemId = item?.itemId ?? item?.id;
  const barcode = String(item?.barcode ?? "").trim().toLowerCase();
  return `item:${itemId ?? "unknown"}:barcode:${barcode}`;
}

export function addOrIncrementCartEntry(cart, entry) {
  const entryKey = getCartEntryKey(entry);
  const existingIndex = cart.findIndex(
    (item) => getCartEntryKey(item) === entryKey,
  );
  if (existingIndex < 0) {
    return [
      ...cart,
      entry.quantity ? { ...entry } : buildCartEntry(entry),
    ];
  }

  const updated = [...cart];
  const existing = updated[existingIndex];
  const quantity = existing.quantity + 1;
  const parsedStock = Number.parseInt(entry.stock, 10);
  updated[existingIndex] = {
    ...existing,
    quantity,
    totalPrice: quantity * existing.unitPrice,
    stock: Number.isInteger(parsedStock)
      ? Math.max(0, parsedStock)
      : existing.stock,
  };
  return updated;
}

export function updateCartEntryQuantity(cart, entryKey, change) {
  return cart.map((item) => {
    if (getCartEntryKey(item) !== entryKey) return item;
    const quantity = Math.max(1, item.quantity + change);
    return { ...item, quantity, totalPrice: quantity * item.unitPrice };
  });
}

export function removeCartEntry(cart, entryKey) {
  return cart.filter((item) => getCartEntryKey(item) !== entryKey);
}

export function buildCheckoutPayload(items) {
  return items.map((item) => {
    const payload = item.unitId
      ? { unit_id: item.unitId, quantity: item.quantity }
      : {
          item_id: item.itemId,
          barcode: String(item.barcode).trim(),
          quantity: item.quantity,
        };
    const unitPrice = Number(item.unitPrice);
    const catalogUnitPrice = Number(item.catalogUnitPrice);

    if (
      Number.isFinite(unitPrice) &&
      unitPrice >= 0 &&
      Number.isFinite(catalogUnitPrice) &&
      unitPrice !== catalogUnitPrice
    ) {
      payload.unit_price_override = unitPrice;
    }

    return payload;
  });
}

export function applySavedStock(carts, barcode, stock, identity = {}) {
  const hasUnitId = identity.unitId !== undefined && identity.unitId !== null;
  const hasItemId = identity.itemId !== undefined && identity.itemId !== null;
  return carts.map((cart) =>
    cart.map((item) =>
      (hasUnitId
        ? String(item.unitId) === String(identity.unitId)
        : hasItemId
          ? String(item.itemId ?? item.id) === String(identity.itemId)
          : String(item.barcode) === String(barcode))
        ? { ...item, stock }
        : item,
    ),
  );
}

const CASHIER_CART_STORAGE_VERSION = 1;
const CASHIER_CART_STORAGE_PREFIX = "groceryStore.cashierCarts";

const emptyCashierCartState = () => ({
  carts: [[], []],
  activeCart: 0,
});

export function getCashierCartStorageKey(user) {
  const identity = user?.id ?? user?.username;
  if (identity === undefined || identity === null || String(identity).trim() === "") {
    return null;
  }
  return `${CASHIER_CART_STORAGE_PREFIX}.v${CASHIER_CART_STORAGE_VERSION}.${encodeURIComponent(String(identity))}`;
}

const normalizePersistedCartItem = (item) => {
  if (!item || typeof item !== "object") return null;

  const barcode = String(item.barcode ?? "").trim();
  const quantity = Number(item.quantity);
  const unitPrice = Number(item.unitPrice);
  const storedCatalogUnitPrice = Number(item.catalogUnitPrice);
  if (
    !barcode ||
    !Number.isSafeInteger(quantity) ||
    quantity < 1 ||
    !Number.isFinite(unitPrice) ||
    unitPrice < 0
  ) {
    return null;
  }

  const stock = Number.parseInt(item.stock, 10);
  const conversionFactor = Number(item.conversionFactor);
  const availableUnits = Number.parseInt(item.availableUnits, 10);
  const normalizedStock = Number.isInteger(stock) ? Math.max(0, stock) : 0;
  const normalizedConversionFactor =
    Number.isFinite(conversionFactor) && conversionFactor > 0
      ? conversionFactor
      : 1;
  const catalogUnitPrice =
    Number.isFinite(storedCatalogUnitPrice) && storedCatalogUnitPrice >= 0
      ? storedCatalogUnitPrice
      : unitPrice;

  return {
    itemId: item.itemId,
    variantId: item.variantId ?? null,
    unitId: item.unitId ?? null,
    barcode,
    name: String(item.name || "Item"),
    quantity,
    unitPrice,
    catalogUnitPrice,
    totalPrice: quantity * unitPrice,
    stock: normalizedStock,
    isVariant: Boolean(item.isVariant),
    isProductUnit: Boolean(item.isProductUnit),
    unitName: String(item.unitName || "Unit"),
    conversionFactor: normalizedConversionFactor,
    availableUnits:
      Number.isInteger(availableUnits) && availableUnits >= 0
        ? availableUnits
        : Math.floor(normalizedStock / normalizedConversionFactor),
  };
};

export function loadCashierCartState(storage, user) {
  const fallback = emptyCashierCartState();
  const storageKey = getCashierCartStorageKey(user);
  if (!storageKey || !storage?.getItem) return fallback;

  try {
    const storedValue = storage.getItem(storageKey);
    if (!storedValue) return fallback;

    const parsed = JSON.parse(storedValue);
    if (
      parsed?.version !== CASHIER_CART_STORAGE_VERSION ||
      !Array.isArray(parsed.carts)
    ) {
      return fallback;
    }

    const carts = [0, 1].map((index) =>
      Array.isArray(parsed.carts[index])
        ? parsed.carts[index]
            .map(normalizePersistedCartItem)
            .filter(Boolean)
        : [],
    );

    return {
      carts,
      activeCart: parsed.activeCart === 1 ? 1 : 0,
    };
  } catch {
    return fallback;
  }
}

export function saveCashierCartState(storage, user, state) {
  const storageKey = getCashierCartStorageKey(user);
  if (!storageKey || !storage?.setItem) return false;

  try {
    storage.setItem(
      storageKey,
      JSON.stringify({
        version: CASHIER_CART_STORAGE_VERSION,
        carts: state.carts,
        activeCart: state.activeCart === 1 ? 1 : 0,
      }),
    );
    return true;
  } catch {
    return false;
  }
}
